import { formatCount } from '@dbrain/app-core/format';
import { LEGAL_DOCUMENTS, VENDOR, formatPlanTokens, formatTry, type PlanDto } from '@dbrain/shared';
import { Icons, OppyWordmark, type IconComponent } from '@dbrain/ui';
import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { usePayments } from '../../api/billing.js';
import { useMe } from '../../api/hooks.js';
import { usePlanCatalog } from '../../api/plan.js';
import { CookieNotice } from '../../components/CookieNotice.js';
import { useMeta } from '../../experience.js';
import { freeMonths } from './CheckoutPage.js';
import { keepLandingPrompt } from './landing-prompt.js';
import { ownModels, sameChatModels, shortNames, tokenTimes } from './site-plans.js';

/**
 * Phase 16c (B2cTanitim, B2cFiyatlar): what a visitor sees at the bireysel address before signing in: the landing page
 * and the prices, with the legal texts in the footer and the cookie note. "Ücretsiz başla" goes to sign-up. While
 * payments are not open the Pro card says so instead of selling. The kurumsal edition has neither page.
 *
 * 16c.2 (B2cTanitim2, B2cFiyatlar2; D-373…): "Önce Oppy'ye sorun." in Oppy's own colours (dark, grey, one blue). The
 * words are the mockups'; every number and name comes from the packages' settings (`GET /api/plans`), written the
 * Turkish way ("1.000 belge"). An example question goes to sign-up and waits in this tab (landing-prompt.ts).
 */
function useTitle(title: string) {
  useEffect(() => {
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);
}

type Tone = 'blue' | 'pink' | 'green' | 'teal' | 'grey' | 'orange';

export function PublicHeader({ active }: { active?: 'features' | 'prices' | 'faq' }) {
  const { t } = useTranslation();
  const me = useMe();
  return (
    <header className="public-bar">
      <div className="public-bar__inner">
        <Link to="/" className="public-bar__brand" aria-label={t('b2cBilling.site.home')}>
          <OppyWordmark className="public-bar__wordmark" />
        </Link>
        <nav className="public-bar__nav" aria-label={t('b2cBilling.site.nav')}>
          <Link to="/#ozellikler" aria-current={active === 'features' ? 'page' : undefined}>
            {t('b2cBilling.site.features')}
          </Link>
          <Link to="/fiyatlar" aria-current={active === 'prices' ? 'page' : undefined}>
            {t('b2cBilling.site.prices')}
          </Link>
          <Link to="/fiyatlar#sss" aria-current={active === 'faq' ? 'page' : undefined}>
            {t('b2cBilling.site.faq')}
          </Link>
        </nav>
        <div className="public-bar__end">
          {me.data ? (
            <Link to="/" className="site-button site-button--dark site-button--sm" data-testid="public-open-app">
              {t('b2cBilling.site.openApp')}
            </Link>
          ) : (
            <>
              <Link to="/login" className="public-bar__login" data-testid="public-login">
                {t('b2cBilling.site.login')}
              </Link>
              <Link to="/signup" className="site-button site-button--dark site-button--sm" data-testid="public-signup">
                {t('b2cBilling.site.start')}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function PublicFooter() {
  const { t } = useTranslation();
  const meta = useMeta().data;
  const legal = [LEGAL_DOCUMENTS.privacy, LEGAL_DOCUMENTS.terms, LEGAL_DOCUMENTS.cookies, LEGAL_DOCUMENTS.distanceSales];
  return (
    <footer className="public-footer">
      <div className="public-footer__inner">
        <p className="public-footer__brand">
          <strong className="public-footer__slogan" data-testid="footer-slogan">
            {t('b2cBilling.site.slogan')}
          </strong>
          <span className="public-footer__sep" aria-hidden="true">
            |
          </span>
          <span>{t('b2cBilling.site.copyright', { year: new Date().getFullYear(), vendor: VENDOR.name })}</span>
        </p>
        <nav className="public-footer__links" aria-label={t('b2cBilling.site.legal')}>
          {legal.map((doc, index) => (
            <Fragment key={doc.slug}>
              {index > 0 ? <span aria-hidden="true">·</span> : null}
              <Link to={`/yasal/${doc.slug}`} data-testid={`footer-${doc.slug}`}>
                {doc.title}
              </Link>
            </Fragment>
          ))}
          {meta?.mailFrom ? (
            <>
              <span aria-hidden="true">·</span>
              <a href={`mailto:${meta.mailFrom}`}>{t('b2cBilling.site.contact')}</a>
            </>
          ) : null}
        </nav>
      </div>
    </footer>
  );
}

export function PublicPage({ active, title, children }: { active?: 'features' | 'prices' | 'faq'; title: string; children: ReactNode }) {
  useTitle(title);
  const location = useLocation();
  // an anchor (#ozellikler, #sss) is reached after the page is drawn
  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ block: 'start' });
  }, [location.hash]);
  return (
    <div className="public" data-testid="public-page">
      <a href="#main" className="skip-link">
        {title}
      </a>
      <PublicHeader {...(active ? { active } : {})} />
      <main id="main" className="public__main">
        {children}
      </main>
      <PublicFooter />
      <CookieNotice />
    </div>
  );
}

/** "NELER YAPAR" over a section's title (the label is written in sentence case; the page sets it in capitals) */
function SectionHead({ id, label, title, lead }: { id: string; label: string; title: string; lead?: string }) {
  return (
    <div className="site-head">
      <span className="site-label">{label}</span>
      <h2 id={id} className="site-head__title">
        {title}
      </h2>
      {lead ? <p className="site-head__lead">{lead}</p> : null}
    </div>
  );
}

function Tick({ size = 15 }: { size?: number }) {
  return <Icons.check size={size} className="site-tick" aria-hidden="true" />;
}

/** "Takıldığınız ilk şeyi şimdi sorun." at the end of both pages */
function DarkBand({ body }: { body: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const titleId = useId();
  return (
    <section className="site-band" aria-labelledby={titleId} data-testid="site-band">
      <div className="site-band__text">
        <h2 id={titleId} className="site-band__title">
          {t('b2cBilling.site.band.title')}
        </h2>
        <p className="site-band__body">{body}</p>
      </div>
      <Link to={me.data ? '/' : '/signup'} className="site-button site-button--white" data-testid="band-start">
        {me.data ? t('b2cBilling.site.openApp') : t('b2cBilling.site.start')}
      </Link>
    </section>
  );
}

/** Commas between a package's parts, as the mockups write them ("Günde 2 milyon token, 2 belge, …") */
function joined(parts: ReactNode[]): ReactNode {
  return parts.map((part, index) => (
    <Fragment key={index}>
      {index > 0 ? ', ' : null}
      {part}
    </Fragment>
  ));
}

/**
 * The short package card's sentence (B2cTanitim2): "Günde 2 milyon token, 2 belge, 3 kendi skill'iniz, 1 bağlantı.
 * Kart gerekmez." and Pro's with its numbers in bold and the free months of a year.
 */
function PlanSentence({ plan, pro }: { plan: PlanDto; pro: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const b = pro ? <strong /> : <span />;
  const skills = plan.maxOwnSkills;
  const connections = plan.maxOwnConnections;
  const parts: ReactNode[] = [
    <Trans key="tokens" i18nKey="b2cBilling.site.plans.tokens" values={{ tokens: formatPlanTokens(plan.dailyTokens, locale) }} components={{ b }} />,
    <Trans key="documents" i18nKey="b2cBilling.site.plans.documents" count={plan.maxDocuments} values={{ value: formatCount(plan.maxDocuments, locale) }} components={{ b }} />,
  ];
  if (skills === null && connections === null) parts.push(t('b2cBilling.site.plans.unlimitedBoth'));
  else {
    if (skills === null) parts.push(t('b2cBilling.site.plans.unlimitedSkills'));
    else if (skills > 0) parts.push(t('b2cBilling.site.plans.skills', { count: skills, value: formatCount(skills, locale) }));
    if (connections === null) parts.push(t('b2cBilling.site.plans.unlimitedConnections'));
    else if (connections > 0) parts.push(t('b2cBilling.site.plans.connections', { count: connections, value: formatCount(connections, locale) }));
  }
  const months = pro ? freeMonths(plan) : 0;
  const tail = pro ? (months > 0 ? t('b2cBilling.site.plans.yearly', { count: months }) : null) : t('b2cBilling.site.plans.noCard');
  return (
    <>
      {joined(parts)}.{tail ? ` ${tail}` : null}
    </>
  );
}

const FEATURES: { key: string; icon: IconComponent; tone: Tone }[] = [
  { key: 'connect', icon: Icons.plug, tone: 'blue' },
  { key: 'documents', icon: Icons.documents, tone: 'pink' },
  { key: 'files', icon: Icons.fileNew, tone: 'green' },
  { key: 'web', icon: Icons.web, tone: 'teal' },
  { key: 'skills', icon: Icons.skills, tone: 'grey' },
  { key: 'memory', icon: Icons.memory, tone: 'orange' },
];

const EXAMPLES: { key: string; icon: IconComponent; tone: Tone }[] = [
  { key: 'contract', icon: Icons.documents, tone: 'pink' },
  { key: 'report', icon: Icons.fileNew, tone: 'green' },
  { key: 'rent', icon: Icons.web, tone: 'teal' },
  { key: 'mail', icon: Icons.chat, tone: 'blue' },
  { key: 'week', icon: Icons.skills, tone: 'grey' },
  { key: 'slides', icon: Icons.fileNew, tone: 'orange' },
];

const STEPS = ['account', 'ask', 'result'] as const;

/**
 * The hero's picture (B2cTanitim2): a conversation drawn in HTML and CSS, not a screenshot. One image to assistive
 * technology, with a sentence saying what it shows; the model's name is Ücretsiz's own.
 */
function HeroShot({ model }: { model: string | null }) {
  const { t } = useTranslation();
  const dues: [string, number][] = [
    ['dues', 3],
    ['bills', 4],
    ['repairs', 7],
  ];
  return (
    <div className="hero-shot" role="img" aria-label={t('b2cBilling.site.shot.label')} data-testid="landing-shot">
      <div className="hero-shot__window" aria-hidden="true">
        <div className="hero-shot__bar">
          <span className="hero-shot__dot hero-shot__dot--red" />
          <span className="hero-shot__dot hero-shot__dot--amber" />
          <span className="hero-shot__dot hero-shot__dot--green" />
          <span className="hero-shot__title">{t('b2cBilling.site.shot.title')}</span>
        </div>
        <div className="hero-shot__body">
          <div className="hero-shot__mine">
            <span className="hero-shot__file">
              <Icons.documents size={14} />
              {t('b2cBilling.site.shot.file')}
            </span>
            <p className="hero-shot__ask">{t('b2cBilling.site.shot.ask')}</p>
          </div>
          <span className="hero-shot__read">
            <Icons.check size={12} />
            {t('b2cBilling.site.shot.read')}
          </span>
          <div className="hero-shot__answer">
            <p>{t('b2cBilling.site.shot.answer')}</p>
            <ul>
              {dues.map(([key, page]) => (
                <li key={key}>
                  <span className="hero-shot__bullet">•</span>
                  <span>
                    {t(`b2cBilling.site.shot.${key}`)}
                    <span className="hero-shot__page">{t('b2cBilling.site.shot.page', { page })}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="hero-shot__sheet">
            <span className="hero-shot__sheet-icon">
              <Icons.spreadsheet size={19} />
            </span>
            <span className="hero-shot__sheet-text">
              <strong>{t('b2cBilling.site.shot.sheet')}</strong>
              <span>{t('b2cBilling.site.shot.sheetNote')}</span>
            </span>
            <span className="hero-shot__download">
              <Icons.download size={14} />
              {t('b2cBilling.site.shot.download')}
            </span>
          </div>
          <div className="hero-shot__composer">
            <span className="hero-shot__placeholder">{t('b2cBilling.site.shot.placeholder')}</span>
            {model ? <span className="hero-shot__model">{model}</span> : null}
            <span className="hero-shot__send">
              <Icons.send size={15} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LandingPage() {
  const { t, i18n } = useTranslation();
  const plans = usePlanCatalog().data ?? [];
  const free = plans.find((plan) => plan.id === 'free');
  const pro = plans.find((plan) => plan.id === 'pro' && plan.onSale);
  const locale = i18n.language;
  // "Ücretsiz pakette 1 bağlantı." from Ücretsiz's setting; nothing at 0
  const freeConnections = free?.maxOwnConnections;
  const connectNote =
    freeConnections === null
      ? t('b2cBilling.site.feature.connect.freeUnlimited')
      : freeConnections !== undefined && freeConnections > 0
        ? t('b2cBilling.site.feature.connect.free', { count: freeConnections, value: formatCount(freeConnections, locale) })
        : null;
  return (
    <PublicPage title={t('b2cBilling.site.landingTitle')}>
      <div className="site-glow site-glow--hero">
        <section className="site-hero" aria-labelledby="landing-title">
          <div className="site-hero__text">
            <p className="site-chip">
              <Icons.skills size={14} aria-hidden="true" />
              {t('b2cBilling.site.chip')}
            </p>
            <h1 id="landing-title" className="site-hero__title" data-testid="landing-title">
              <span className="site-hero__ask">{t('b2cBilling.site.ask')}</span>{' '}
              <span className="site-hero__main">{t('b2cBilling.site.hero')}</span>
            </h1>
            <p className="site-hero__lead">
              <Trans i18nKey="b2cBilling.site.lead" components={{ b: <strong /> }} />
            </p>
            <div className="site-hero__actions">
              <Link to="/signup" className="site-button site-button--dark site-button--lg" data-testid="landing-start">
                {t('b2cBilling.site.start')}
              </Link>
              <Link to="/fiyatlar" className="site-button site-button--light site-button--lg" data-testid="landing-prices">
                {t('b2cBilling.site.seePrices')}
              </Link>
            </div>
            <ul className="site-ticks" data-testid="landing-ticks">
              <li>
                <Tick />
                {t('b2cBilling.site.ticks.card')}
              </li>
              {free ? (
                <li data-testid="landing-tick-tokens">
                  <Tick />
                  {t('b2cBilling.site.ticks.tokens', { tokens: formatPlanTokens(free.dailyTokens, locale) })}
                </li>
              ) : null}
              <li>
                <Tick />
                {t('b2cBilling.site.ticks.renew')}
              </li>
            </ul>
          </div>
          <HeroShot model={free?.chatModels[0]?.name ?? null} />
        </section>
      </div>

      <section id="ozellikler" className="site-section" aria-labelledby="landing-what">
        <SectionHead id="landing-what" label={t('b2cBilling.site.what.label')} title={t('b2cBilling.site.what.title')} lead={t('b2cBilling.site.what.lead')} />
        <div className="site-features">
          {FEATURES.map(({ key, icon: Icon, tone }) => (
            <article key={key} className="site-feature" data-testid={`landing-feature-${key}`}>
              <span className={`site-tile site-tile--${tone}`} aria-hidden="true">
                <Icon size={21} />
              </span>
              <h3 className="site-feature__title">{t(`b2cBilling.site.feature.${key}.title`)}</h3>
              <p className="site-feature__body">
                {t(`b2cBilling.site.feature.${key}.body`)}
                {key === 'connect' && connectNote ? <span data-testid="landing-connect-free"> {connectNote}</span> : null}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="site-section site-section--ruled" aria-labelledby="landing-how">
        <SectionHead id="landing-how" label={t('b2cBilling.site.how.label')} title={t('b2cBilling.site.how.title')} />
        <ol className="site-steps">
          {STEPS.map((key, index) => (
            <li key={key} className="site-step">
              <span className={index === 0 ? 'site-step__n site-step__n--first' : 'site-step__n'} aria-hidden="true">
                {index + 1}
              </span>
              <strong className="site-step__title">{t(`b2cBilling.site.how.${key}.title`)}</strong>
              <span className="site-step__body">{t(`b2cBilling.site.how.${key}.body`)}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="site-section site-section--ruled" aria-labelledby="landing-try">
        <SectionHead id="landing-try" label={t('b2cBilling.site.try.label')} title={t('b2cBilling.site.try.title')} lead={t('b2cBilling.site.try.lead')} />
        <ul className="site-examples">
          {EXAMPLES.map(({ key, icon: Icon, tone }) => {
            const text = t(`b2cBilling.site.try.examples.${key}`);
            return (
              <li key={key}>
                <Link to="/signup" className="site-example" onClick={() => keepLandingPrompt(text)} data-testid={`landing-example-${key}`}>
                  <span className={`site-tile site-tile--sm site-tile--${tone}`} aria-hidden="true">
                    <Icon size={17} />
                  </span>
                  <span className="site-example__text">{text}</span>
                  <Icons.send size={15} className="site-example__go" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="site-section site-section--ruled site-section--plans" aria-labelledby="landing-plans">
        <SectionHead id="landing-plans" label={t('b2cBilling.site.plans.label')} title={t('b2cBilling.site.plans.title')} lead={t('b2cBilling.site.plans.lead')} />
        <div className="site-plans">
          {free ? (
            <article className="site-plan" aria-labelledby="landing-plan-free" data-testid="landing-plan-free">
              <h3 id="landing-plan-free" className="site-plan__name">
                {free.name}
              </h3>
              <p className="site-plan__price">
                <strong>{formatTry(0, locale, true)}</strong>
              </p>
              <p className="site-plan__lines" data-testid="landing-plan-free-lines">
                <PlanSentence plan={free} pro={false} />
              </p>
            </article>
          ) : null}
          {pro ? (
            <article className="site-plan site-plan--pro" aria-labelledby="landing-plan-pro" data-testid="landing-plan-pro">
              <div className="site-plan__head">
                <h3 id="landing-plan-pro" className="site-plan__name">
                  {pro.name}
                </h3>
                <span className="site-badge">
                  <Icons.skills size={11} aria-hidden="true" />
                  {t('b2cBilling.prices.proFor')}
                </span>
              </div>
              <p className="site-plan__price">
                <strong data-testid="landing-plan-pro-price">{formatTry(pro.monthlyPriceKurus, locale, true)}</strong>
                <span>{t('b2cBilling.site.plans.perMonth')}</span>
              </p>
              <p className="site-plan__lines" data-testid="landing-plan-pro-lines">
                <PlanSentence plan={pro} pro />
              </p>
            </article>
          ) : null}
        </div>
        <Link to="/fiyatlar" className="site-link" data-testid="landing-compare">
          {t('b2cBilling.site.plans.compare')}
        </Link>
      </section>

      <div className="site-band-wrap">
        <DarkBand body={t('b2cBilling.site.band.landing')} />
      </div>
    </PublicPage>
  );
}

/** Aylık / Yıllık (B2cFiyatlar2): a radio group, the chosen one dark; arrows move between the two */
function PeriodSwitch({ value, onChange, months }: { value: 'monthly' | 'yearly'; onChange: (next: 'monthly' | 'yearly') => void; months: number }) {
  const { t } = useTranslation();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const options = ['monthly', 'yearly'] as const;
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const next = value === 'monthly' ? 'yearly' : 'monthly';
    onChange(next);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={t('b2cBilling.prices.period')} className="site-period" onKeyDown={onKeyDown} data-testid="prices-period">
      {options.map((option) => (
        <button
          key={option}
          ref={(node) => {
            refs.current[option] = node;
          }}
          type="button"
          role="radio"
          aria-checked={value === option}
          tabIndex={value === option ? 0 : -1}
          className="site-period__option"
          onClick={() => onChange(option)}
          data-testid={`prices-period-${option}`}
        >
          {t(`b2cBilling.periods.${option}`)}
          {/* a space for the radio's name ("Yıllık 2 ay bedava"); the flex gap draws the distance */}
          {option === 'yearly' && months > 0 ? (
            <>
              {' '}
              <span className="site-period__save">{t('b2cBilling.checkout.freeMonths', { count: months })}</span>
            </>
          ) : null}
        </button>
      ))}
    </div>
  );
}

type Cell = string | boolean;

function CompareCell({ value }: { value: Cell }) {
  const { t } = useTranslation();
  if (typeof value === 'string') return <>{value}</>;
  return value ? (
    <Icons.check size={17} className="site-tick" aria-label={t('b2cBilling.prices.yes')} />
  ) : (
    <span className="site-compare__no" aria-label={t('b2cBilling.prices.no')}>
      —
    </span>
  );
}

export function PricingPage() {
  const { t, i18n } = useTranslation();
  const plans = usePlanCatalog().data ?? [];
  const payments = usePayments();
  const me = useMe();
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const free = plans.find((plan) => plan.id === 'free');
  const pro = plans.find((plan) => plan.id === 'pro' && plan.onSale);
  const locale = i18n.language;
  const months = pro ? freeMonths(pro) : 0;
  const count = (value: number) => formatCount(value, locale);
  // D-375: what both packages have is said once, in the strip; the same chat models move there too
  const sharedModels = free && pro ? sameChatModels(free, pro) && free.chatModels.length > 0 : false;
  const names = (plan: PlanDto) => plan.chatModels.map((m) => m.name).join(', ');
  const limitCell = (value: number | null): Cell => (value === null ? t('b2cBilling.prices.unlimited') : value > 0 ? count(value) : false);
  const plain = <span />;
  const bold = <strong />;

  const lines = (plan: PlanDto, other: PlanDto | undefined, isPro: boolean): ReactElement[] => {
    const b = isPro ? bold : plain;
    const times = isPro ? tokenTimes(free, plan) : null;
    const rows: { key: string; off?: boolean; content: ReactNode }[] = [
      {
        key: 'tokens',
        content: (
          <>
            <Trans i18nKey="b2cBilling.prices.lines.tokens" values={{ tokens: formatPlanTokens(plan.dailyTokens, locale) }} components={{ b }} />
            {times ? <span className="site-times"> {t('b2cBilling.prices.lines.times', { times: count(times) })}</span> : null}
          </>
        ),
      },
      { key: 'documents', content: <Trans i18nKey="b2cBilling.prices.lines.documents" count={plan.maxDocuments} values={{ value: count(plan.maxDocuments) }} components={{ b }} /> },
      { key: 'files', content: <Trans i18nKey="b2cBilling.prices.lines.files" values={{ mb: count(plan.maxFileMb) }} components={{ b }} /> },
    ];
    if (!sharedModels) {
      // Ücretsiz names its model; Pro its own beside Ücretsiz's ("Büyük, Orta, Hızlı dahil")
      const own = isPro ? ownModels(plan, other) : plan.chatModels;
      const listed = isPro && own.length > 0 && own.length < plan.chatModels.length ? t('b2cModels.offer.modelsIncluded', { names: shortNames(own.map((m) => m.name)) }) : names(plan);
      rows.push({
        key: 'models',
        off: plan.chatModels.length === 0,
        content: <Trans i18nKey="b2cBilling.prices.lines.models" count={plan.chatModels.length} values={{ value: count(plan.chatModels.length), names: listed }} components={{ b }} />,
      });
    }
    const skills = plan.maxOwnSkills;
    rows.push({
      key: 'skills',
      off: skills === 0,
      content:
        skills === null ? (
          <Trans i18nKey="b2cBilling.prices.lines.unlimitedSkills" components={{ b }} />
        ) : skills === 0 ? (
          t('b2cBilling.prices.lines.noSkills')
        ) : (
          t('b2cBilling.prices.lines.skills', { count: skills, value: count(skills) })
        ),
    });
    const connections = plan.maxOwnConnections;
    rows.push({
      key: 'connections',
      off: connections === 0,
      content:
        connections === null ? (
          <Trans i18nKey="b2cBilling.prices.lines.unlimitedConnections" components={{ b }} />
        ) : connections === 0 ? (
          t('b2cBilling.prices.lines.noConnections')
        ) : (
          t('b2cBilling.prices.lines.connections', { count: connections, value: count(connections) })
        ),
    });
    return rows.map((row) => (
      <li key={row.key} className={row.off ? 'site-line site-line--off' : 'site-line'} data-testid={`price-line-${row.key}`}>
        {row.off ? (
          <span className="site-line__dash" aria-hidden="true">
            –
          </span>
        ) : (
          <Tick size={16} />
        )}
        <span>{row.content}</span>
      </li>
    ));
  };

  const shared: { key: string; icon: IconComponent; tone: Tone; label: string }[] = [
    ...(sharedModels && free ? [{ key: 'models', icon: Icons.chat, tone: 'blue' as const, label: names(free) }] : []),
    { key: 'web', icon: Icons.web, tone: 'teal', label: t('b2cBilling.prices.shared.web') },
    { key: 'files', icon: Icons.fileNew, tone: 'green', label: t('b2cBilling.prices.shared.files') },
    { key: 'memory', icon: Icons.memory, tone: 'orange', label: t('b2cBilling.prices.shared.memory') },
    { key: 'library', icon: Icons.skills, tone: 'grey', label: t('b2cBilling.prices.shared.library') },
  ];

  const proPrice = pro ? (period === 'yearly' ? (pro.yearlyPriceKurus ?? pro.monthlyPriceKurus * 12) : pro.monthlyPriceKurus) : 0;
  const groups: { key: string; label: string; rows: { key: string; label: string; free: Cell; pro: Cell }[] }[] =
    free && pro
      ? [
          {
            key: 'usage',
            label: t('b2cBilling.prices.groups.usage'),
            rows: [
              { key: 'tokens', label: t('b2cBilling.prices.rows.tokens'), free: formatPlanTokens(free.dailyTokens, locale), pro: formatPlanTokens(pro.dailyTokens, locale) },
              { key: 'documents', label: t('b2cBilling.prices.rows.documents'), free: count(free.maxDocuments), pro: count(pro.maxDocuments) },
              { key: 'files', label: t('b2cBilling.prices.rows.fileSize'), free: t('b2cBilling.prices.mb', { mb: count(free.maxFileMb) }), pro: t('b2cBilling.prices.mb', { mb: count(pro.maxFileMb) }) },
            ],
          },
          {
            key: 'personal',
            label: t('b2cBilling.prices.groups.personal'),
            rows: [
              { key: 'skills', label: t('b2cBilling.prices.rows.skills'), free: limitCell(free.maxOwnSkills), pro: limitCell(pro.maxOwnSkills) },
              { key: 'connections', label: t('b2cBilling.prices.rows.connections'), free: limitCell(free.maxOwnConnections), pro: limitCell(pro.maxOwnConnections) },
            ],
          },
          {
            key: 'features',
            label: t('b2cBilling.prices.groups.features'),
            rows: [
              {
                key: 'models',
                label: t('b2cBilling.prices.rows.models', { count: Math.max(free.chatModels.length, pro.chatModels.length) }),
                free: free.chatModels.length > 0 ? names(free) : false,
                pro: pro.chatModels.length > 0 ? names(pro) : false,
              },
              { key: 'basics', label: t('b2cBilling.prices.rows.basics'), free: true, pro: true },
              { key: 'make', label: t('b2cBilling.prices.rows.files'), free: true, pro: true },
            ],
          },
        ]
      : [];

  // "Ücretsiz pakette 1, Pro'da sınırsız." from both packages' settings
  const connectionWord = (value: number | null) => (value === null ? t('b2cBilling.prices.faq.mcp.unlimited') : value === 0 ? t('b2cBilling.prices.faq.mcp.none') : count(value));
  const faq: { key: string; q: string; a: string }[] = (['token', 'used', 'cancel', 'invoice', 'mcp', 'data'] as const).map((key) => ({
    key,
    q: t(`b2cBilling.prices.faq.${key}.q`),
    a:
      key === 'mcp' && free && pro
        ? `${t('b2cBilling.prices.faq.mcp.a')} ${t('b2cBilling.prices.faq.mcp.limits', { free: connectionWord(free.maxOwnConnections), pro: connectionWord(pro.maxOwnConnections) })}`
        : t(`b2cBilling.prices.faq.${key}.a`),
  }));

  return (
    <PublicPage active="prices" title={t('b2cBilling.prices.title')}>
      <div className="site-glow site-glow--prices">
        <section className="site-prices-head" aria-labelledby="prices-title">
          <span className="site-label">{t('b2cBilling.prices.label')}</span>
          <h1 id="prices-title" className="site-prices-head__title" data-testid="prices-title">
            <span>{t('b2cBilling.prices.heading')}</span> <span className="site-prices-head__more">{t('b2cBilling.prices.headingMore')}</span>
          </h1>
          <p className="site-prices-head__lead">{t('b2cBilling.prices.lead')}</p>
          {pro && pro.yearlyPriceKurus !== null ? <PeriodSwitch value={period} onChange={setPeriod} months={months} /> : null}
        </section>

        <div className={pro ? 'site-price-cards' : 'site-price-cards site-price-cards--one'}>
          {free ? (
            <article className="site-price-card" aria-labelledby="price-free-name" data-testid="price-free">
              <div className="site-price-card__head">
                <h2 id="price-free-name" className="site-price-card__name">
                  {free.name}
                </h2>
                <span className="site-price-card__for">{t('b2cBilling.prices.freeFor')}</span>
              </div>
              <div className="site-price-card__price">
                <strong className="site-price-card__amount">{formatTry(0, locale, true)}</strong>
                <span className="site-price-card__note">{t('b2cBilling.prices.noCard')}</span>
              </div>
              <Link to={me.data ? '/' : '/signup'} className="site-button site-button--light site-button--block" data-testid="price-free-start">
                {me.data ? t('b2cBilling.site.openApp') : t('b2cBilling.site.start')}
              </Link>
              <ul className="site-lines">{lines(free, pro, false)}</ul>
            </article>
          ) : null}
          {pro ? (
            <article className="site-price-card site-price-card--pro" aria-labelledby="price-pro-name" data-testid="price-pro">
              <div className="site-price-card__title">
                <h2 id="price-pro-name" className="site-price-card__name">
                  {pro.name}
                </h2>
                <span className="site-badge">
                  <Icons.skills size={12} aria-hidden="true" />
                  {t('b2cBilling.prices.proFor')}
                </span>
              </div>
              <div className="site-price-card__price">
                <span className="site-price-card__row">
                  <strong className="site-price-card__amount" data-testid="price-pro-amount">
                    {formatTry(proPrice, locale, true)}
                  </strong>
                  <span className="site-price-card__per">{t(`b2cBilling.prices.per.${period}`)}</span>
                </span>
                <span className="site-price-card__note" data-testid="price-pro-note">
                  {period === 'monthly' && pro.yearlyPriceKurus !== null ? (
                    months > 0 ? (
                      <Trans i18nKey="b2cBilling.prices.proNoteYearly" count={months} values={{ price: formatTry(pro.yearlyPriceKurus, locale, true), count: months }} components={{ b: <strong className="site-save" /> }} />
                    ) : (
                      t('b2cBilling.prices.proNoteYearlyPlain', { price: formatTry(pro.yearlyPriceKurus, locale, true) })
                    )
                  ) : (
                    t('b2cBilling.prices.proNote')
                  )}
                </span>
              </div>
              {payments.open ? (
                <Link to={me.data ? `/odeme?period=${period}` : '/signup'} className="site-button site-button--dark site-button--block" data-testid="price-pro-buy">
                  {t('b2c.subscription.upgrade')}
                </Link>
              ) : (
                <div className="site-soon" data-testid="price-pro-soon" data-guest={me.data ? undefined : 'true'}>
                  <span className="site-soon__icon" aria-hidden="true">
                    {me.data ? <Icons.mail size={17} /> : <Icons.time size={17} />}
                  </span>
                  {/* 16c.3: a visitor who is not signed in gets no promise of a mail (none is asked before sign-up) */}
                  <span className="site-soon__text">
                    <strong>{me.data ? t('b2cBilling.prices.soon') : t('b2cBilling.prices.soonGuest')}</strong>
                    <span>{me.data ? t('b2cBilling.prices.soonBody') : t('b2cBilling.prices.soonBodyGuest')}</span>
                  </span>
                </div>
              )}
              <ul className="site-lines site-lines--pro">{lines(pro, free, true)}</ul>
            </article>
          ) : null}
        </div>

        {free ? (
          <div className="site-shared" data-testid="prices-shared">
            <strong className="site-shared__label">{pro ? t('b2cBilling.prices.shared.label') : t('b2cBilling.prices.shared.labelOne')}</strong>
            <ul className="site-shared__items">
              {shared.map(({ key, icon: Icon, tone, label }) => (
                <li key={key} className="site-shared__item" data-testid={`prices-shared-${key}`}>
                  <span className={`site-tile site-tile--xs site-tile--${tone}`} aria-hidden="true">
                    <Icon size={15} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {groups.length > 0 && free && pro ? (
          <section className="site-compare" aria-labelledby="compare-title">
            <h2 id="compare-title" className="site-compare__title">
              {t('b2cBilling.prices.compare')}
            </h2>
            <div className="site-compare__wrap">
              <table className="site-compare__table" data-testid="prices-compare">
                <colgroup>
                  <col className="site-compare__col-feature" />
                  <col />
                  <col className="site-compare__col-pro" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">{t('b2cBilling.prices.feature')}</th>
                    <th scope="col">{free.name}</th>
                    <th scope="col" className="site-compare__pro-head">
                      {pro.name}
                    </th>
                  </tr>
                </thead>
                {groups.map((group) => (
                  <tbody key={group.key} data-testid={`prices-group-${group.key}`}>
                    <tr className="site-compare__group">
                      <th scope="rowgroup" colSpan={3}>
                        {group.label}
                      </th>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.key} data-testid={`prices-row-${row.key}`}>
                        <th scope="row">{row.label}</th>
                        <td>
                          <CompareCell value={row.free} />
                        </td>
                        <td className="site-compare__pro">
                          <CompareCell value={row.pro} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          </section>
        ) : null}

        <section id="sss" className="site-faq" aria-labelledby="faq-title">
          <h2 id="faq-title" className="site-faq__title">
            {t('b2cBilling.site.faq')}
          </h2>
          <div className="site-faq__grid">
            {faq.map(({ key, q, a }) => (
              <article key={key} className="site-faq__item" data-testid={`faq-${key}`}>
                <h3 className="site-faq__q">{q}</h3>
                <p className="site-faq__a">{a}</p>
              </article>
            ))}
          </div>
        </section>

        <div className="site-band-wrap site-band-wrap--prices">
          <DarkBand body={t('b2cBilling.site.band.prices')} />
        </div>
      </div>
    </PublicPage>
  );
}
